import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AIAnalysisPanel = ({ isVisible, onToggle, selectedRequirement }) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(null);

  const mockAnalysisResults = {
    confidence: 87,
    recommendation: "Gap - Custom Development Required",
    reasoning: `Based on the requirement analysis, this functionality requires custom development as SAP standard processes don't fully support the specific workflow described.\n\nKey gaps identified:\n• Custom approval matrix beyond standard SAP\n• Integration with legacy system not supported\n• Specific reporting format requirements`,
    suggestedApproach: "Develop custom workflow using SAP BTP and integrate with standard procurement processes",
    effortEstimate: "15-20 days",
    riskFactors: [
      "Integration complexity with legacy systems",
      "Custom approval logic maintenance",
      "Potential upgrade impact"
    ],
    alternatives: [
      "Modify business process to fit SAP standard",
      "Use third-party workflow tool",
      "Implement as enhancement to standard process"
    ]
  };

  const handleAnalyze = async () => {
    setIsAnalyzing(true);
    
    // Simulate AI analysis
    setTimeout(() => {
      setAnalysisResults(mockAnalysisResults);
      setIsAnalyzing(false);
    }, 2000);
  };

  const handleApplyRecommendation = () => {
    console.log('Applying AI recommendation to requirement');
    // This would update the requirement with AI suggestions
  };

  if (!isVisible) {
    return (
      <div className="fixed right-4 top-1/2 transform -translate-y-1/2 z-50">
        <Button
          variant="default"
          size="sm"
          iconName="Bot"
          onClick={onToggle}
          className="rounded-full shadow-elevation-2"
        >
          AI Assistant
        </Button>
      </div>
    );
  }

  return (
    <div className="fixed right-4 top-20 bottom-4 w-80 bg-card border border-border rounded-lg shadow-elevation-3 z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-2">
          <Icon name="Bot" size={20} className="text-accent" />
          <h3 className="text-sm font-semibold text-foreground">AI Analysis</h3>
        </div>
        <Button
          variant="ghost"
          size="sm"
          iconName="X"
          onClick={onToggle}
        />
      </div>
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {!selectedRequirement ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <Icon name="MousePointer" size={32} className="text-muted-foreground/50 mb-3" />
            <p className="text-sm text-muted-foreground mb-2">
              Select a requirement card to get AI-powered analysis
            </p>
            <p className="text-xs text-muted-foreground">
              AI will analyze fit/gap and provide recommendations
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Current Requirement */}
            <div className="p-3 bg-muted/50 rounded-lg">
              <h4 className="text-sm font-medium text-foreground mb-1">
                Analyzing Requirement
              </h4>
              <p className="text-xs text-muted-foreground">
                {selectedRequirement?.title}
              </p>
            </div>

            {/* Analysis Button */}
            {!analysisResults && (
              <Button
                variant="default"
                fullWidth
                loading={isAnalyzing}
                iconName="Zap"
                onClick={handleAnalyze}
                disabled={isAnalyzing}
              >
                {isAnalyzing ? 'Analyzing...' : 'Run AI Analysis'}
              </Button>
            )}

            {/* Analysis Results */}
            {analysisResults && (
              <div className="space-y-4">
                {/* Confidence Score */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-muted-foreground">
                      Confidence Score
                    </span>
                    <span className="text-sm font-medium text-foreground">
                      {analysisResults?.confidence}%
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div 
                      className="h-2 bg-accent rounded-full"
                      style={{ width: `${analysisResults?.confidence}%` }}
                    />
                  </div>
                </div>

                {/* Recommendation */}
                <div>
                  <h5 className="text-xs font-medium text-muted-foreground mb-2">
                    Recommendation
                  </h5>
                  <div className="p-3 bg-accent/10 border border-accent/20 rounded-lg">
                    <p className="text-sm font-medium text-accent">
                      {analysisResults?.recommendation}
                    </p>
                  </div>
                </div>

                {/* Reasoning */}
                <div>
                  <h5 className="text-xs font-medium text-muted-foreground mb-2">
                    Analysis Reasoning
                  </h5>
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="text-xs text-foreground whitespace-pre-line">
                      {analysisResults?.reasoning}
                    </p>
                  </div>
                </div>

                {/* Suggested Approach */}
                <div>
                  <h5 className="text-xs font-medium text-muted-foreground mb-2">
                    Suggested Approach
                  </h5>
                  <p className="text-xs text-foreground">
                    {analysisResults?.suggestedApproach}
                  </p>
                </div>

                {/* Effort Estimate */}
                <div>
                  <h5 className="text-xs font-medium text-muted-foreground mb-2">
                    Effort Estimate
                  </h5>
                  <p className="text-sm font-medium text-foreground">
                    {analysisResults?.effortEstimate}
                  </p>
                </div>

                {/* Risk Factors */}
                <div>
                  <h5 className="text-xs font-medium text-muted-foreground mb-2">
                    Risk Factors
                  </h5>
                  <div className="space-y-1">
                    {analysisResults?.riskFactors?.map((risk, index) => (
                      <div key={index} className="flex items-start space-x-2">
                        <Icon name="AlertTriangle" size={12} className="text-warning mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-foreground">{risk}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Alternatives */}
                <div>
                  <h5 className="text-xs font-medium text-muted-foreground mb-2">
                    Alternative Approaches
                  </h5>
                  <div className="space-y-1">
                    {analysisResults?.alternatives?.map((alt, index) => (
                      <div key={index} className="flex items-start space-x-2">
                        <Icon name="Lightbulb" size={12} className="text-accent mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-foreground">{alt}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="space-y-2 pt-2 border-t border-border">
                  <Button
                    variant="default"
                    size="sm"
                    fullWidth
                    iconName="CheckCircle"
                    onClick={handleApplyRecommendation}
                  >
                    Apply Recommendation
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    fullWidth
                    iconName="RotateCcw"
                    onClick={() => setAnalysisResults(null)}
                  >
                    Run New Analysis
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AIAnalysisPanel;